// =========================================================================
// AlertsScene — per-surface projections. Fork-owned (zone C).
//
// Transformation layer: pure functions, no React, no fetches, no mutation.
//
// Source renderers: `alertPage`, `renderAlerts` and `approve` at lines
// 2279–2293 of `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`.
//
// @see docs/logic/issues/alerts.md
// =========================================================================

import { ALERTS, DEFAULT_ALERT_ID, type Alert, type AlertEvidenceRef } from '@/versions/v1/shared/seed/alerts';

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

/**
 * resolveAlert(alertId) → Alert
 * Projection rule: the demo's `ALERTS.find(x => x.id === ALERT_CUR) || ALERTS[0]`.
 *   An unknown or absent id falls back to `DEFAULT_ALERT_ID` rather than
 *   rendering an empty page, so a stale deep link still lands somewhere real.
 */
export function resolveAlert(alertId: string | undefined): Alert {
  const byId = alertId ? ALERTS.find((a) => a.id === alertId) : undefined;
  return byId ?? ALERTS.find((a) => a.id === DEFAULT_ALERT_ID) ?? ALERTS[0];
}

/** `true` when the id names an alert — drives the "not found" note. */
export function isKnownAlert(alertId: string | undefined): boolean {
  return Boolean(alertId) && ALERTS.some((a) => a.id === alertId);
}

/**
 * alertsCountLabel() → string
 * Derive: the demo's `#alerts-count` chip — `7 open · 3 live`.
 */
export function alertsCountLabel(): string {
  const live = ALERTS.filter((a) => a.mode === 'LIVE').length;
  return `${ALERTS.length} open · ${live} live`;
}

// ---------------------------------------------------------------------------
// Emphasis
// ---------------------------------------------------------------------------

/** One run of a chain step's output: plain, or re-bolded from `emphasis`. */
export interface EmphasisSegment {
  readonly text: string;
  readonly bold: boolean;
}

/**
 * emphasisSegments(text, emphasis) → EmphasisSegment[]
 *
 * The seed stripped the demo's `<b>…</b>` out of every chain output and kept
 * the bolded substrings in `emphasis`, in order of appearance. This walks the
 * sentence once, consuming each emphasis in turn, and returns the runs.
 *
 * Filter: an emphasis that no longer occurs in the text is skipped rather than
 *   throwing — the sentence still renders, just without that bold.
 * Invariant: concatenating `segment.text` reproduces `text` exactly.
 */
export function emphasisSegments(
  text: string,
  emphasis: readonly string[] | undefined,
): EmphasisSegment[] {
  if (!emphasis || emphasis.length === 0) return [{ text, bold: false }];

  const segments: EmphasisSegment[] = [];
  let cursor = 0;

  for (const needle of emphasis) {
    if (!needle) continue;
    const at = text.indexOf(needle, cursor);
    if (at === -1) continue;
    if (at > cursor) segments.push({ text: text.slice(cursor, at), bold: false });
    segments.push({ text: needle, bold: true });
    cursor = at + needle.length;
  }

  if (cursor < text.length) segments.push({ text: text.slice(cursor), bold: false });
  return segments.length ? segments : [{ text, bold: false }];
}

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

/** Where an evidence chip's "open" control goes. */
export type EvidenceTarget =
  /** A slice of a connected system's table — opens the `oem-source` drawer. */
  | { readonly kind: 'dataset'; readonly dataset: string; readonly raw: string }
  /** A file in the lake — navigates to the data browser with the file selected. */
  | { readonly kind: 'document'; readonly to: string; readonly raw: string };

/**
 * evidenceTarget(ref, machineId) → EvidenceTarget
 *
 * Projection rule: mirrors the four branches of the demo's `alertPage()`
 *   evidence renderer.
 *   - `dataset`      → `audit(key, { machine_id })`, i.e. the source drawer
 *   - `document`     → `openDoc(file)`
 *   - `drillPlan`    → `openDoc('plan:drillplan')`
 *   - `emailThread`  → `show('data'); openDoc('eml:11', true)`
 *
 *   The last three all resolve to a file in the lake, and the port has one
 *   place that opens a file: the data browser. They therefore share a route,
 *   with the file carried in `?doc=`. The raw token travels alongside on every
 *   branch so the citation stays readable even when the target is unported.
 */
export function evidenceTarget(ref: AlertEvidenceRef, machineId: string): EvidenceTarget {
  if (ref.kind === 'dataset') {
    return { kind: 'dataset', dataset: ref.dataset, raw: ref.raw };
  }
  void machineId;
  return {
    kind: 'document',
    to: `/integration/data?doc=${encodeURIComponent(ref.file)}`,
    raw: ref.raw,
  };
}

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------

/**
 * approvalKey(alertId, stepIndex) → string
 * Invariant: the demo keys its approve buttons `ap-<alertId>-<i>`; two steps
 *   on the same alert can carry the same agent and label, so the index is the
 *   only thing that separates them.
 */
export function approvalKey(alertId: string, stepIndex: number): string {
  return `${alertId}::${stepIndex}`;
}

/** The demo's post-approval pill text (`approve()`, line 2293). */
export const APPROVED_LABEL = 'Approved · planner';
